import * as INR from "../INR.ts";
import ApiServer from "../ApiServer.ts";
import { getStartSequence } from "../actions.ts";
import * as FwDatas from "../FwDatas.ts";
import * as login from "../features/login.ts";
import * as i18n from "../i18n.ts";
import * as libLocation from "../lib/location.ts";
import { clearAuthorization, hasAuthorization } from "../server/Authorization.ts";
import { getHomeHubAuthUrl, isHomeHubAuthenticationContext } from "./auth-continuation.ts";
import {
  getOfferCode,
  hasValidatedOfferCode,
  markOfferCodeValidated,
  resetActivationJourney
} from "./journey-state.ts";
import { getCommercialDisplay } from "./commercial-config.ts";
import { getHomeHubCheckoutUrl, validateOfferCode, validateOfferCodeFormat } from "./offer-code.ts";
import {
  SEGMENT_COUNT,
  applyBackspace,
  applyInputValue,
  applyPaste,
  boxValue,
  emptySegments,
  joinSegments
} from "./offer-code-segments.ts";
import { checkPremiumEligibility } from "./premium-eligibility-service.ts";
import {
  getFactsToCollect,
  resolvePageRequirements,
  type JourneyFacts,
  type PageRequirement,
  type UnsatisfiedRequirement
} from "./page-requirements.ts";
import { registerDom } from "../util/Html.ts";
import { waitForPremium } from "../modal/waitingPremium.ts";

// Home Hub help center landing page. Every support CTA in this journey points
// here rather than at a mailto: — the Home Hub webview has no mail handler and
// dead-ends on ERR_UNKNOWN_URL_SCHEME.
const HELP_CENTER_URL = "https://homehubsupport.familywall.com/hc/en-us";
// Home Hub setup guide, linked from the get-started dispatcher.
const SETUP_GUIDE_URL = "https://shop.familywall.com/pages/set-up-size";
// Task-specific help article. Locale segment hard-coded to English until the
// support site's per-locale URL scheme is confirmed — do not swap in a guessed
// locale code here, as unverified paths would 404.
const SUPPORT_LOCALE = "en";
const CANCEL_PREMIUM_URL = `https://support.familywall.com/${SUPPORT_LOCALE}/support/solutions/articles/47001239194-cancel-your-subscription-or-trial`;
const TOTAL_JOURNEY_STEPS = 8;

// The ${supportLink} anchor embedded in homehubActivation.code.errors.notFound.
function getSupportLink(): string {
  return `<a href="${HELP_CENTER_URL}" target="_blank" rel="noopener noreferrer">`
    + i18n.get("homehubActivation.code.errors.supportLinkLabel")
    + "</a>";
}

function getCover(className: string, titleKey: string, bodyKey?: string) {
  return {
    className,
    brand: i18n.get("homehubActivation.cover.brand"),
    title: i18n.get(titleKey),
    body: bodyKey ? i18n.get(bodyKey) : ""
  };
}

function getProgress(currentStep: number) {
  return {
    dots: Array.from({ length: TOTAL_JOURNEY_STEPS }, function(_, index) {
      const step = index + 1;
      return { className: step < currentStep ? "is-complete" : step === currentStep ? "is-current" : "" };
    })
  };
}

function getOfferBenefits() {
  return ["calendar", "meals", "budget", "birthdays", "documents", "more"].map(function(key) {
    return { label: i18n.get(`homehubActivation.offer.benefit.${key}`) };
  });
}

function getAccountEmail() {
  const identifiers = FwDatas.getUser()?.identifiers || [];
  return identifiers.find(identifier => identifier.type === "Email")?.value
    || identifiers.find(identifier => identifier.type === "Login")?.value
    || i18n.get("homehubActivation.review.accountUnavailable");
}

const REQUIREMENT_URLS: Record<UnsatisfiedRequirement, string> = {
  offer_code: INR.URL.HOMEHUB_ACTIVATION_OFFER,
  authentication: getHomeHubAuthUrl(INR.URL.FW2_SIGNIN),
  already_authenticated: INR.URL.HOMEHUB_ACTIVATION_REVIEW,
  family: getHomeHubAuthUrl(INR.URL.FW2_SIGNUP_FAMILY),
  terms: getHomeHubAuthUrl(INR.URL.FW2_TERMS),
  active_premium: INR.URL.HOMEHUB_ACTIVATION_ACTIVE_PREMIUM,
  checkout: INR.URL.HOMEHUB_ACTIVATION_CHECKOUT_READY
};

async function collectJourneyFacts(requirements: readonly PageRequirement[]): Promise<JourneyFacts> {
  const toCollect = getFactsToCollect(requirements);
  const facts: JourneyFacts = {
    hasOfferCode: hasValidatedOfferCode(),
    isAuthenticated: hasAuthorization()
  };
  if (!facts.isAuthenticated) return facts;

  if (toCollect.onboarding) {
    const [account, families] = await Promise.all([
      ApiServer.accgetloggedaccount(),
      ApiServer.famlistfamily(true)
    ]);
    facts.hasFamilies = families.length > 0;
    facts.termsChecked = account.termsChecked === true;
  }
  if (toCollect.eligibility) {
    facts.eligibility = await checkPremiumEligibility();
  }
  return facts;
}

/**
 * The single start sequence for every Home Hub activation page.
 *
 * A page declares the minimum it needs; this resolves those needs against what is
 * actually observable (session offer code, authorization token, account state,
 * Premium credits) and either renders the page or redirects to the page that
 * lets the customer obtain the missing piece.
 */
function getHomeHubStartSequence(
  requirements: readonly PageRequirement[],
  getPageData: (facts: JourneyFacts)=>Record<string, unknown> = () => ({})
) {
  const requiresAuthentication = getFactsToCollect(requirements).authentication;
  return getStartSequence(requiresAuthentication, async function(callback, data) {
    let facts: JourneyFacts;
    try {
      facts = await collectJourneyFacts(requirements);
    } catch {
      // The token is present but unusable, so the customer has to sign in again.
      clearAuthorization();
      libLocation.goTo(REQUIREMENT_URLS.authentication, true);
      return;
    }

    const resolution = resolvePageRequirements(requirements, facts);
    if (resolution.status === "redirect") {
      libLocation.goTo(REQUIREMENT_URLS[resolution.to], true);
      return;
    }
    callback({ ...data, ...getPageData(facts) });
  });
}

// OneTrust "Manage Preferences" leaves the Home Hub webview frozen under its dark
// overlay, so the journey hides the whole consent UI. Without consent, OneTrust
// keeps the trackers blocked. The class stays until the next full load.
function hideConsentBanner() {
  document.documentElement.classList.add("homehub-no-consent-banner");
}

function registerPageTitle(url: string, titleKey: string) {
  INR.onPage(url, function() {
    hideConsentBanner();
    const previousTitle = document.title;
    document.title = i18n.get(titleKey);
    INR.subscribe("pageUnload", function() {
      document.title = previousTitle;
    });
  });
}

INR.createPage(
  INR.URL.HOMEHUB_GET_STARTED,
  getHomeHubStartSequence([], function() {
    return {
      setupGuideUrl: SETUP_GUIDE_URL,
      supportHomeUrl: HELP_CENTER_URL,
      noCover: true
    };
  }),
  function() {
    return Promise.all([import("./page_dispatcher.mustache")]);
  },
  "fw2"
);

INR.createPage(
  INR.URL.HOMEHUB_ACTIVATION_OFFER,
  getHomeHubStartSequence([], function() {
    return {
      progress: getProgress(1),
      benefits: getOfferBenefits(),
      offerPriceLine: getCommercialDisplay().offerPriceLine,
      cover: getCover(
        "homehub-activation-cover--offer",
        "homehubActivation.offer.cover.title",
        "homehubActivation.offer.cover.description"
      )
    };
  }),
  function() {
    return Promise.all([import("./page_offer.mustache")]);
  },
  "fw2"
);

INR.createPage(
  INR.URL.HOMEHUB_ACTIVATION_CODE,
  getHomeHubStartSequence([], function() {
    return {
      progress: getProgress(2),
      segments: Array.from({ length: SEGMENT_COUNT }, function(_, index) {
        return { index };
      }),
      cover: getCover(
        "homehub-activation-cover--offer",
        "homehubActivation.code.cover.title",
        "homehubActivation.code.cover.description"
      )
    };
  }),
  function() {
    return Promise.all([import("./page_code-entry.mustache")]);
  },
  "fw2"
);

INR.createPage(
  INR.URL.HOMEHUB_ACCOUNT_CHOICE,
  getHomeHubStartSequence(["offer_code", "anonymous"], function() {
    return {
      signInUrl: REQUIREMENT_URLS.authentication,
      signUpUrl: getHomeHubAuthUrl(INR.URL.FW2_SIGNUP),
      progress: getProgress(3),
      cover: getCover(
        "homehub-activation-cover--entry",
        "homehubActivation.accountChoice.cover.title",
        "homehubActivation.accountChoice.cover.description"
      )
    };
  }),
  function() {
    return Promise.all([import("./page_account-choice.mustache")]);
  },
  "fw2"
);

INR.createPage(
  INR.URL.HOMEHUB_ACTIVATION_REVIEW,
  getHomeHubStartSequence(["offer_code", "onboarding", "eligible_for_offer"], function() {
    const commercial = getCommercialDisplay();
    return {
      accountEmail: getAccountEmail(),
      dueTodayValue: commercial.dueTodayValue,
      renewalValue: commercial.renewalValue,
      progress: getProgress(5),
      cover: getCover(
        "homehub-activation-cover--review",
        "homehubActivation.review.cover.title",
        "homehubActivation.review.cover.description"
      )
    };
  }),
  function() {
    return Promise.all([import("./page_review.mustache")]);
  },
  "fw2"
);

INR.createPage(
  INR.URL.HOMEHUB_ACTIVATION_ACTIVE_PREMIUM,
  getHomeHubStartSequence(
    ["offer_code", "onboarding", "active_premium"],
    function(facts) {
      return {
        cancelPremiumUrl: CANCEL_PREMIUM_URL,
        contactSupportUrl: HELP_CENTER_URL,
        currentPremiumPeriodEnd: facts.eligibility?.status === "active"
          ? facts.eligibility.currentPeriodEnd
          : undefined,
        progress: getProgress(5),
        cover: getCover(
          "homehub-activation-cover--review",
          "homehubActivation.activePremium.cover.title",
          "homehubActivation.activePremium.cover.description"
        )
      };
    }
  ),
  function() {
    return Promise.all([import("./page_active-premium.mustache")]);
  },
  "fw2"
);

INR.createPage(
  INR.URL.HOMEHUB_ACTIVATION_STATUS_UNAVAILABLE,
  // Only reached from the activation-complete wait timing out: the purchase is done
  // but Premium never became visible. No eligibility requirement, since requiring
  // the status this page reports on would loop.
  getHomeHubStartSequence(["offer_code", "onboarding"], function() {
    return {
      accountEmail: getAccountEmail(),
      contactSupportUrl: HELP_CENTER_URL,
      progress: getProgress(5),
      cover: getCover(
        "homehub-activation-cover--review",
        "homehubActivation.statusUnavailable.cover.title",
        "homehubActivation.statusUnavailable.cover.description"
      )
    };
  }),
  function() {
    return Promise.all([import("./page_status-unavailable.mustache")]);
  },
  "fw2"
);


INR.createPage(
  INR.URL.HOMEHUB_ACTIVATION_CHECKOUT_READY,
  getHomeHubStartSequence(
    ["offer_code", "onboarding", "eligible_for_offer"],
    function() {
      return {
        progress: getProgress(6),
        cover: getCover(
          "homehub-activation-cover--review",
          "homehubActivation.checkoutReady.cover.title",
          "homehubActivation.checkoutReady.cover.description"
        )
      };
    }
  ),
  function() {
    return Promise.all([import("./page_checkout-ready.mustache")]);
  },
  "fw2"
);

INR.createPage(
  INR.URL.HOMEHUB_ACTIVATION_COMPLETE,
  // No eligibility requirement: the purchase is confirmed server-side and the
  // credits may not be granted yet when this page opens — waitForPremium observes it.
  getHomeHubStartSequence(
    ["offer_code", "onboarding"],
    function() {
      return {
        accountEmail: getAccountEmail(),
        progress: getProgress(7),
        cover: getCover(
          "homehub-activation-cover--review",
          "homehubActivation.activationComplete.cover.title",
          "homehubActivation.activationComplete.cover.description"
        )
      };
    }
  ),
  function() {
    return Promise.all([import("./page_activation-complete.mustache")]);
  },
  "fw2"
);
INR.onPage(INR.URL.HOMEHUB_ACTIVATION_COMPLETE,async function(){
  if(await waitForPremium()){
    return;
  }
  //the purchase went through but Premium never became visible, which is the only
  //situation the status-unavailable screen describes.
  libLocation.goTo(INR.URL.HOMEHUB_ACTIVATION_STATUS_UNAVAILABLE,true);
})



INR.createPage(
  INR.URL.HOMEHUB_SIGNIN,
  getHomeHubStartSequence(["authentication"], function() {
    return {
      accountEmail: getAccountEmail(),
      contactSupportUrl: HELP_CENTER_URL,
      progress: getProgress(8),
      cover: getCover(
        "homehub-activation-cover--review",
        "homehubActivation.homeHubSignIn.cover.title",
        "homehubActivation.homeHubSignIn.cover.description"
      )
    };
  }),
  function() {
    return Promise.all([import("./page_homehub-signin.mustache")]);
  },
  "fw2"
);

registerPageTitle(INR.URL.HOMEHUB_GET_STARTED, "homehubActivation.dispatcher.pageTitle");
registerPageTitle(INR.URL.HOMEHUB_ACTIVATION_OFFER, "homehubActivation.offer.pageTitle");
registerPageTitle(INR.URL.HOMEHUB_ACTIVATION_CODE, "homehubActivation.code.pageTitle");
registerPageTitle(INR.URL.HOMEHUB_ACCOUNT_CHOICE, "homehubActivation.accountChoice.pageTitle");
registerPageTitle(INR.URL.HOMEHUB_ACTIVATION_REVIEW, "homehubActivation.review.pageTitle");
registerPageTitle(INR.URL.HOMEHUB_ACTIVATION_STATUS_UNAVAILABLE, "homehubActivation.statusUnavailable.pageTitle");
registerPageTitle(INR.URL.HOMEHUB_ACTIVATION_ACTIVE_PREMIUM, "homehubActivation.activePremium.pageTitle");
registerPageTitle(INR.URL.HOMEHUB_ACTIVATION_CHECKOUT_READY, "homehubActivation.checkoutReady.pageTitle");
registerPageTitle(INR.URL.HOMEHUB_ACTIVATION_COMPLETE, "homehubActivation.activationComplete.pageTitle");
registerPageTitle(INR.URL.HOMEHUB_SIGNIN, "homehubActivation.homeHubSignIn.pageTitle");

[INR.URL.FW2_SIGNIN, INR.URL.FW2_SIGNUP, INR.URL.FW2_SIGNUP_FAMILY, INR.URL.FW2_TERMS].forEach(function(url) {
  INR.onPage(url, function() {
    if (isHomeHubAuthenticationContext()) hideConsentBanner();
  });
});

INR.onPage(INR.URL.HOMEHUB_GET_STARTED, function() {
  resetActivationJourney();
});

INR.onPage(INR.URL.HOMEHUB_ACTIVATION_CODE, function() {
  const form = document.querySelector<HTMLFormElement>(".homehub-code-form");
  const error = document.querySelector<HTMLElement>("#homehub-code-error");
  const boxes = Array.from(document.querySelectorAll<HTMLInputElement>(".homehub-code-segments__box"));
  if (!form || !error || boxes.length !== SEGMENT_COUNT) return;

  let segments = emptySegments();

  function render(focusIndex?: number) {
    boxes.forEach(function(box, index) {
      box.value = boxValue(segments[index]);
      box.classList.remove("has-error");
    });
    if (focusIndex !== undefined) boxes[focusIndex]?.focus();
  }

  const supportLink = getSupportLink();

  function clearError() {
    error.hidden = true;
    error.textContent = "";
  }

  function showError(messageKey: string) {
    boxes.forEach(function(box) {
      box.classList.add("has-error");
    });
    error.hidden = false;
    // notFound embeds a ${supportLink} anchor; incomplete is plain text.
    error.innerHTML = i18n.get(messageKey, { supportLink });
    boxes[0]?.focus();
  }

  function backspace(index: number) {
    const update = applyBackspace(segments, index);
    segments = update.segments;
    clearError();
    render(update.focusIndex);
  }

  boxes.forEach(function(box, index) {
    // The value is the source of truth: Android IMEs (Home Hub WebView) send
    // keydown as "Unidentified", so deletions and overwrites only show up here.
    box.addEventListener("input", function() {
      const update = applyInputValue(segments, index, box.value);
      segments = update.segments;
      clearError();
      render(update.focusIndex);
    });

    box.addEventListener("keydown", function(event) {
      if (event.key === "Backspace" || event.keyCode === 8) {
        event.preventDefault();
        backspace(index);
      } else if (event.key === "ArrowLeft" && index > 0) {
        boxes[index - 1].focus();
      } else if (event.key === "ArrowRight" && index < SEGMENT_COUNT - 1) {
        boxes[index + 1].focus();
      }
    });

    box.addEventListener("paste", function(event) {
      event.preventDefault();
      const text = event.clipboardData?.getData("text") || "";
      const update = applyPaste(segments, index, text);
      segments = update.segments;
      clearError();
      render(update.focusIndex);
    });
    // Caret after the filler or the character, so that the IME backspace deletes
    // it; deferred because a tap places the caret after the focus event.
    function placeCaretAtEnd() {
      setTimeout(function() {
        box.setSelectionRange(box.value.length, box.value.length);
      }, 0);
    }
    box.addEventListener("focus", placeCaretAtEnd);
    box.addEventListener("click", placeCaretAtEnd);
  });
  // Put the filler in the empty boxes before the first keystroke.
  render();

  form.addEventListener("submit", async function(event) {
    event.preventDefault();
    const code = joinSegments(segments);
    const formatError = validateOfferCodeFormat(code);
    if (formatError) {
      showError("homehubActivation.code.errors.incomplete");
      return;
    }

    const submitButton = form.querySelector<HTMLButtonElement>("button[type=submit]");
    if (submitButton) submitButton.disabled = true;

    const result = await validateOfferCode(code);
    if (!result.valid) {
      if (submitButton) submitButton.disabled = false;
      showError(
        result.reason === "not_found"
          ? "homehubActivation.code.errors.notFound"
          : "homehubActivation.code.errors.incomplete"
      );
      return;
    }

    markOfferCodeValidated(result.code);
    libLocation.goTo(INR.URL.HOMEHUB_ACCOUNT_CHOICE);
  });
});
registerDom({
  homehubchangeaccount(){
    login.logoutAndGoTo(REQUIREMENT_URLS.authentication);
  },
  async homehubcheckout(){
    const button = this as HTMLButtonElement;
    const code = getOfferCode();
    if (!code || button.disabled) return;
    const error = document.querySelector<HTMLElement>("#homehub-checkout-error");
    // Disabled until the page leaves, so a second tap cannot start another checkout.
    button.disabled = true;
    if (error) error.hidden = true;
    const result = await getHomeHubCheckoutUrl(code);
    if (!result.checkoutUrl) {
      button.disabled = false;
      if (error) {
        error.hidden = false;
        error.innerHTML = result.failure === "code_refused"
          ? i18n.get("homehubActivation.code.errors.notFound", { supportLink: getSupportLink() })
          : i18n.get("fw2.defaultError");
      }
      return;
    }
    window.location.href = result.checkoutUrl;
  }
})